import { i18n } from '@/plugins/i18n';
import store from '@/store';

import {
  getRecurrenceBreakdown,
  getLabelsAndDatasetsFromRecurrence,
  recurrenceChartOptions,
} from '@/utils/vhub-utils';
import VitalityCriterion from '@/utils/classes/VitalityCategory/VitalityCriterion';

/** Score based on the number of occurrences of problems in GW */
export default new VitalityCriterion({
  titleTr: 'VH_GW_PB_RECURRENCE',
  key: 'gwPbRecurrence',
  weight: 2,
  defaultChartIndex: 1,

  score: ({ completedGws }, { start, end }) => {
    // Filter completed GW for the period
    const filtered = completedGws.filter(gw => start.isBefore(gw.date) && end.isAfter(gw.date));

    const { pbTypesPerId, zonesPerId } = store.getters;

    const pbPerItem = [];
    const pbPerZone = [];
    const pbPerType = [];

    const nGW = filtered.length;
    let nProblems = 0;
    let gw, step, checklist, item, pb, j, jlen, k, klen, l, llen, m, mlen;
    for (let i = 0; i < nGW; i++) {
      gw = filtered[i];
      for (j = 0, jlen = gw.steps.length; j < jlen; j++) {
        step = gw.steps[j];
        for (k = 0, klen = step.checklists.length; k < klen; k++) {
          checklist = step.checklists[k];
          for (l = 0, llen = checklist.items.length; l < llen; l++) {
            item = checklist.items[l];
            for (m = 0, mlen = item.problems.length; m < mlen; m++) {
              nProblems++;
              pb = item.problems[m];
              pb.types.forEach((typeId) => {
                pbPerType.push(pbTypesPerId[`${typeId}`]);
              });
              pb.zones.forEach((zoneId) => {
                pbPerZone.push(zonesPerId[`${zoneId}`]);
              });
              pbPerItem.push(item);
            }
          }
        }
      }
    }

    const typesBreakdown = getRecurrenceBreakdown(pbPerType, 'id', 'name', undefined, 100);
    const zonesBreakdown = getRecurrenceBreakdown(pbPerZone, 'id', 'name', undefined, 100);
    const itemsBreakdown = getRecurrenceBreakdown(pbPerItem, 'template', 'templateText', undefined, 100);
    const score = nGW === 0 ? 0 : (typesBreakdown.score + zonesBreakdown.score + itemsBreakdown.score) / 3;

    return {
      score,
      data: {
        noTopsFlops: nGW === 0,
        noCategoryHint: true,
        value: score,
        nGW,
        nProblems,
        typesRecurrence: typesBreakdown.itemsPerRecurrenceId,
        zonesRecurrence: zonesBreakdown.itemsPerRecurrenceId,
        itemsRecurrence: itemsBreakdown.itemsPerRecurrenceId,
        maxType: typesBreakdown.maxOccurrences,
        maxZone: zonesBreakdown.maxOccurrences,
        maxTypeText: typesBreakdown.maxText,
        maxZoneText: zonesBreakdown.maxText,
      },
    };
  },

  text({
    nGW,
    nProblems,
    maxType,
    maxZone,
    maxTypeText,
    maxZoneText,
  }) {
    if (nGW === 0) {
      return i18n.tc('message.N_PROBLEMS', 0, { nb: 0 });
    } else if (nProblems > 1) {
      const zone = maxZone > 1;
      const type = maxType > 1;
      const maxTypePerc = (maxType * 100 / nProblems).sToFixed(1);
      const maxZonePerc = (maxZone * 100 / nProblems).sToFixed(1);
      const trParams = {
        typePerc: maxTypePerc,
        zonePerc: maxZonePerc,
        typeText: maxTypeText,
        zoneText: maxZoneText,
      };

      if (zone && type) {
        return i18n.t('message.VH_PB_TYPE_ZONE_TEXT_BOTH', trParams);
      } else if (zone) {
        return i18n.t('message.VH_PB_TYPE_ZONE_TEXT_ONLY_Z', trParams);
      } else if (type) {
        return i18n.t('message.VH_PB_TYPE_ZONE_TEXT_ONLY_T', trParams);
      }
    }
    return i18n.t('message.VH_PB_NO_RECURRENCE_TEXT');
  },

  chartData(teamGroups) {
    // Display a stacked bar charts. The zone (and type) where most problems occur will appear first
    const itemsLabelsAndData = getLabelsAndDatasetsFromRecurrence(teamGroups, this.scoresKey, 'itemsRecurrence');
    return [
      itemsLabelsAndData,
      // Keep colors from the first calculation
      getLabelsAndDatasetsFromRecurrence(teamGroups, this.scoresKey, 'zonesRecurrence', itemsLabelsAndData.colorsSet),
      getLabelsAndDatasetsFromRecurrence(teamGroups, this.scoresKey, 'typesRecurrence', itemsLabelsAndData.colorsSet),
    ];
  },

  chartOptions() {
    return [recurrenceChartOptions, recurrenceChartOptions, recurrenceChartOptions];
  },

  chartNames() {
    return [i18n.t('message.GW_CL_ITEMS'), i18n.t('message.LOCATIONS'), i18n.t('message.TYPES')];
  },
});
